<template>
    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('documents.distributeTitle')"></page-title>
    </div>

    <div class="p-6" v-if="!result">
        <p class="text-sm text-gray-600 mb-4">
            {{ $t("documents.distributeDescription") }}
        </p>

        <p
            v-if="unexpectedError"
            data-test="unexpected-error"
            class="text-sm text-red-600 mb-4"
        >
            {{ $t("errors.unexpected") }}
        </p>

        <div class="col-span-6 mb-4">
            <label class="block text-sm font-medium text-gray-700">
                {{ $t("documents.name") }} <RequiredMark />
            </label>
            <input
                type="text"
                v-model="name"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                autofocus
            />
            <jet-input-error :message="fieldError('name')" class="mt-2" />
        </div>

        <div class="col-span-6 mb-4">
            <label class="block text-sm font-medium text-gray-700">
                {{ $t("documents.file") }} <RequiredMark />
            </label>
            <FileDropzone
                :accept="allowedMimesAccept"
                @file="onDropFile"
                @reject="onFileRejected"
            >
                <div class="mt-1 flex items-center">
                    <div class="mt-2 w-full" v-show="fileUploaded">
                        <span class="block">{{ fileUploaded }}</span>
                    </div>

                    <jet-secondary-button
                        class="mt-2 mr-2"
                        type="button"
                        @click.prevent="selectNewFile"
                        v-if="!fileUploaded"
                        >{{ $t("actions.chooseFile") }}</jet-secondary-button
                    >
                    <jet-secondary-button
                        class="mt-2 mr-2"
                        type="button"
                        @click.prevent="clearFileInput"
                        v-if="fileUploaded"
                        >{{ $t("actions.removeFile") }}</jet-secondary-button
                    >

                    <input
                        type="file"
                        class="hidden"
                        ref="file"
                        :accept="allowedMimesAccept"
                        @change="updateFileUploaded"
                    />
                </div>
            </FileDropzone>
            <p class="my-3 text-sm text-gray-400">
                {{
                    $t("documents.fileInfo", {
                        size: maxSizeInMb,
                        extensions: documentMimes.toUpperCase().replace(/,/g, " / "),
                    })
                }}
            </p>
            <jet-input-error :message="fieldError('file')" class="mt-2" />
        </div>

        <!-- Ziel und Umfang sind beide eine Wahl aus zwei gleichwertigen
             Möglichkeiten und tragen deshalb dieselbe Pillen-Form. -->
        <div class="col-span-6 mb-4">
            <p
                id="distribute-target-label"
                class="text-xs uppercase font-semibold tracking-widest text-gray-500 mb-1"
            >
                {{ $t("documents.distributeTarget") }}
            </p>
            <!-- Die Pillen ersetzen Radio-Knoepfe und tragen deren Semantik:
                 ohne `role`/`aria-checked` waere der gewaehlte Zustand allein
                 an der Farbe erkennbar (WCAG 1.4.1 und 4.1.2). -->
            <div
                class="flex gap-2"
                role="radiogroup"
                aria-labelledby="distribute-target-label"
            >
                <button
                    type="button"
                    role="radio"
                    :aria-checked="target === 'overview'"
                    :class="pillClass(target === 'overview')"
                    @click="target = 'overview'"
                >
                    {{ $t("documents.distributeTargetOverview") }}
                </button>
                <button
                    type="button"
                    role="radio"
                    :aria-checked="target === 'topic'"
                    :class="pillClass(target === 'topic')"
                    @click="target = 'topic'"
                >
                    {{ $t("documents.distributeTargetTopic") }}
                </button>
            </div>
        </div>

        <div class="col-span-6 mb-4" v-if="target === 'topic'">
            <label class="block text-sm font-medium text-gray-700">
                {{ $t("documents.distributeTopicName") }} <RequiredMark />
            </label>
            <select
                v-model="topicName"
                class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
            >
                <option value="" disabled>
                    {{ $t("documents.distributeTopicPlaceholder") }}
                </option>
                <option v-for="topic in topicNames" :key="topic" :value="topic">
                    {{ topic }}
                </option>
            </select>
            <jet-input-error :message="fieldError('topic_name')" class="mt-2" />
        </div>

        <!-- Der Regelfall ist "an alle Wohnungen". Die vollstaendige Liste
             stand vorher immer offen, obwohl sie nur fuer den Ausnahmefall
             gebraucht wird — sie erscheint jetzt erst unter "Auswahl". -->
        <p
            id="distribute-scope-label"
            class="text-xs uppercase font-semibold tracking-widest text-gray-500 mb-1"
        >
            {{ $t("documents.distributeApartments") }}
        </p>
        <div
            class="flex gap-2 mb-2"
            role="radiogroup"
            aria-labelledby="distribute-scope-label"
        >
            <button
                type="button"
                role="radio"
                :aria-checked="scope === 'all'"
                :class="pillClass(scope === 'all')"
                @click="scope = 'all'"
            >
                {{ $t("documents.distributeScopeAll", { count: apartments.length }) }}
            </button>
            <button
                type="button"
                role="radio"
                :aria-checked="scope === 'selection'"
                :class="pillClass(scope === 'selection')"
                @click="scope = 'selection'"
            >
                {{ $t("documents.distributeScopeSelection") }}
            </button>
        </div>

        <template v-if="scope === 'selection'">
            <div class="flex justify-end mb-1">
                <button
                    type="button"
                    class="text-xs text-minplan-blue hover:underline"
                    @click="toggleAll"
                >
                    {{
                        allSelected
                            ? $t("exports.archiveDeselectAll")
                            : $t("exports.archiveSelectAll")
                    }}
                </button>
            </div>
            <apartment-selection-list
                :apartments="apartments"
                v-model="selected"
            />
        </template>

        <p class="text-sm text-gray-500 mb-4">
            {{
                $t("documents.distributeSummary", {
                    count: effectiveSelection.length,
                    target:
                        target === "topic"
                            ? topicName || $t("documents.distributeTargetTopic")
                            : $t("documents.distributeTargetOverview"),
                })
            }}
        </p>
        <jet-input-error :message="fieldError('apartments')" class="mt-2" />

        <label class="flex items-center mb-2">
            <input type="checkbox" class="rounded mr-2" v-model="notify" />
            <span class="text-sm text-gray-700">{{
                $t("documents.distributeNotify")
            }}</span>
        </label>
    </div>

    <div class="p-6" v-else>
        <p class="text-sm text-gray-900 mb-4">
            {{
                $t("documents.distributeResultSummary", {
                    distributed: result.distributed,
                    total: result.total,
                })
            }}
        </p>

        <template v-if="result.skipped.length">
            <span class="text-xs uppercase font-semibold tracking-widest text-gray-500">
                {{ $t("documents.distributeSkipped") }}
            </span>
            <ul class="divide-y divide-gray-100 border rounded mt-2">
                <li
                    v-for="(skip, index) in result.skipped"
                    :key="index"
                    class="px-3 py-2 text-sm"
                >
                    <span class="font-medium text-gray-900">{{ skip.apartment }}</span>
                    <span class="text-gray-500">
                        — {{ skipReasonLabel(skip.reason) }}</span
                    >
                </li>
            </ul>
        </template>
    </div>

    <div class="flex justify-end my-4 mx-6">
        <button
            @click="cancel"
            type="button"
            class="inline-block uppercase font-semibold text-xs tracking-widest"
        >
            {{ result ? $t("actions.close") : $t("actions.cancel") }}
        </button>

        <primary-button
            v-if="!result"
            type="button"
            @click="submit"
            :class="{ 'opacity-25': submitting }"
            :disabled="submitting || !canSubmit"
            class="ml-3"
        >
            <span v-if="submitting">{{ $t("actions.saving") }}</span>
            <span v-else>{{ $t("documents.distributeSubmit") }}</span>
        </primary-button>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import axios from "axios";
import MainLayout from "@/Layouts/MainLayout";
import ApartmentSelectionList from "@/Shared/Components/Apartments/ApartmentSelectionList";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetInputError from "@/Jetstream/InputError";
import PageTitle from "@/Shared/Components/PageTitle";
import RequiredMark from "@/Shared/Components/RequiredMark";
import FileDropzone from "@/Shared/Components/FileDropzone.vue";
import documentFileField from "@/Shared/Mixins/documentFileField.js";

// Welle 4, Thema 8 (Spec
// docs/superpowers/plans/2026-08-05-welle4-thema8-dokumente-verteilen.md,
// Entscheid 1): Submit läuft per axios/FormData (binärer Upload, bestehende
// Axios-Ausnahme) statt `useForm` — die Antwort ist ein JSON-Envelope, kein
// Redirect. Das Modal bleibt offen und rendert den Bericht selbst.
//
// `documentFileField`-Mixin (R3 Punkt 1): `maxSizeInMb`/`documentMimes`/
// `allowedMimesAccept` + Auswahl-/Entfernen-/Drop-Handling kommen von dort.
// `setFile()` bleibt beim Mixin-Default (`this.file`), kein Override nötig.
export default defineComponent({
    props: ["project", "apartments", "topicNames"],
    inheritAttrs: false,
    emits: ["closeModal"],
    layout: MainLayout,
    mixins: [documentFileField],
    components: {
        ApartmentSelectionList,
        PageTitle,
        PrimaryButton,
        JetSecondaryButton,
        JetInputError,
        RequiredMark,
        FileDropzone,
    },
    data() {
        return {
            name: "",
            file: null,
            fileUploaded: null,
            target: "overview",
            topicName: "",
            // "all" heisst: alle Wohnungen des Projekts, auch solche, die erst
            // nach dem Oeffnen des Modals dazukaemen — deshalb wird die Liste
            // beim Absenden aus `apartments` gebildet und nicht eingefroren.
            // `selected` traegt ausschliesslich die manuelle Auswahl.
            scope: "all",
            selected: this.apartments.map((apartment) => apartment.uuid),
            notify: false,
            submitting: false,
            errors: {},
            result: null,
            unexpectedError: false,
        };
    },
    computed: {
        allSelected() {
            return (
                this.apartments.length > 0 &&
                this.selected.length === this.apartments.length
            );
        },
        // Die tatsaechlich verteilte Menge — bei "Alle" der volle Bestand,
        // bei "Auswahl" die Haekchen.
        effectiveSelection() {
            return this.scope === "all"
                ? this.apartments.map((apartment) => apartment.uuid)
                : this.selected;
        },
        canSubmit() {
            return (
                !!this.name &&
                !!this.file &&
                this.effectiveSelection.length > 0 &&
                (this.target !== "topic" || !!this.topicName)
            );
        },
    },
    methods: {
        // Gemeinsame Form fuer die beiden Zweifach-Entscheide (Ziel, Umfang).
        pillClass(active) {
            return [
                "px-3 py-1.5 rounded-full text-sm border transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-minplan-blue",
                active
                    ? "bg-minplan-blue text-white border-minplan-blue"
                    : "bg-white text-gray-600 border-gray-300 hover:border-minplan-blue",
            ];
        },
        fieldError(field) {
            return this.errors[field] ? this.errors[field][0] : null;
        },
        skipReasonLabel(reason) {
            const key = {
                no_matching_topic: "noMatchingTopic",
                error: "error",
                not_found: "notFound",
            }[reason];

            return key ? this.$t(`documents.distributeSkipReasons.${key}`) : reason;
        },
        toggleAll() {
            this.selected = this.allSelected
                ? []
                : this.apartments.map((apartment) => apartment.uuid);
        },
        cancel() {
            this.$emit("closeModal");
        },
        submit() {
            if (this.submitting || !this.canSubmit) return;

            this.submitting = true;
            this.errors = {};
            this.unexpectedError = false;

            const formData = new FormData();
            formData.append("name", this.name);
            formData.append("file", this.file);
            formData.append("target", this.target);
            if (this.target === "topic") {
                formData.append("topic_name", this.topicName);
            }
            this.effectiveSelection.forEach((uuid) =>
                formData.append("apartments[]", uuid)
            );
            formData.append("notify", this.notify ? "1" : "0");

            axios
                .post(
                    this.route("advisors.projects.documents.distribute", {
                        project: this.project.uuid,
                    }),
                    formData
                )
                .then((response) => {
                    this.result = response.data;
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.errors = error.response.data.errors || {};
                    } else {
                        // R3 Punkt 6 (BACKLOG §5 Welle-4 (l)): 403/500 etc.
                        // liefen bisher ohne Rückmeldung durch (nur Konsole).
                        console.error(error);
                        this.unexpectedError = true;
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
});
</script>

<style scoped></style>
